import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Adult tables (approximate calculator.net values)
const ADULT = {
  men:   { US:[6,6.5,7,7.5,8,8.5,9,9.5,10,10.5,11,11.5,12,12.5,13,14,15],
           UK:[5.5,6,6.5,7,7.5,8,8.5,9,9.5,10,10.5,11,11.5,12,12.5,13.5,14.5],
           EU:[39,39.5,40,40.5,41,42,42.5,43,44,44.5,45,45.5,46,46.5,47,48,49],
           JP:[24,24.5,25,25.5,26,26.5,27,27.5,28,28.5,29,29.5,30,30.5,31,32,33],
           CM:[24,24.5,25,25.5,26,26.5,27,27.5,28,28.5,29,29.5,30,30.5,31,32,33] },
  women: { US:[4,4.5,5,5.5,6,6.5,7,7.5,8,8.5,9,9.5,10,10.5,11,11.5,12],
           UK:[2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8,8.5,9,9.5,10],
           EU:[35,35.5,36,36.5,37,37.5,38,38.5,39,39.5,40,40.5,41,41.5,42,42.5,43],
           JP:[21,21.5,22,22.5,23,23.5,24,24.5,25,25.5,26,26.5,27,27.5,28,28.5,29],
           CM:[21,21.5,22,22.5,23,23.5,24,24.5,25,25.5,26,26.5,27,27.5,28,28.5,29] }
};

const KIDS = { // kids / big kid (US → EU / UK / cm)
  US:[8,8.5,9,9.5,10,10.5,11,11.5,12,12.5,13,13.5,1,1.5,2,2.5,3],
  UK:[7,7.5,8,8.5,9,9.5,10,10.5,11,11.5,12,12.5,13,13.5,1,1.5,2],
  EU:[25,25.5,26,26.5,27,27.5,28,28.5,29,29.5,30,30.5,31,31.5,32,33,34],
  CM:[14.5,15,15.5,16,16.5,17,17.5,18,18.5,19,19.5,20,20.5,21,21.5,22,22.5]
};

const INFANT = { // infant / toddler
  US:[0,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8],
  UK:[0,0.5,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5],
  EU:[15,16,17,17.5,18,18.5,19,19.5,20,20.5,21,22,22.5,23,23.5,24],
  CM:[7,8,9,9.5,10,10.5,11,11.5,12,12.5,13,13.5,14,14.5,15,15.5]
};

export default function ShoeSizeCalc() {
  const [mode, setMode] = useState('convert'); // convert | from-length
  const [group, setGroup] = useState('men'); // men | women | kids | infant
  const [from, setFrom] = useState('US');
  const [value, setValue] = useState('9');
  const [length, setLength] = useState(26); // cm
  const [lengthUnit, setLengthUnit] = useState('cm');

  const table = group === 'kids' ? KIDS : group === 'infant' ? INFANT : ADULT[group];
  const cols = Object.keys(table);

  const row = useMemo(() => {
    if (mode === 'convert') {
      const arr = table[from];
      const idx = arr.findIndex(v => String(v) === String(value));
      if (idx < 0) return null;
      const out = {}; cols.forEach(c => out[c] = table[c][idx]); return out;
    }
    // by length in cm
    let cm = Number(length); if (lengthUnit === 'in') cm = cm * 2.54; if (lengthUnit === 'mm') cm = cm / 10;
    const arr = table.CM || table.CM;
    let idx = 0, best = Infinity;
    arr.forEach((v, i) => { const d = Math.abs(v - cm); if (d < best) { best = d; idx = i; } });
    const out = {}; cols.forEach(c => out[c] = table[c][idx]); return out;
  }, [mode, group, from, value, length, lengthUnit, table, cols]);

  const options = table[from] || [];

  return (
    <CalcShell look="cnet" title="Shoe Size Converter" subtitle="Convert shoe sizes across US, UK, EU, JP and CM for men, women, kids and infants.">
      <div className="flex gap-2 border-b mb-4">
        {[['convert','By Size'],['from-length','By Foot Length']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)} data-testid={`shoe-mode-${v}`}>{l}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field">
            <label>Group</label>
            <select value={group} onChange={e => { setGroup(e.target.value); setFrom('US'); }} data-testid="shoe-group">
              <option value="men">Men (Adult)</option>
              <option value="women">Women (Adult)</option>
              <option value="kids">Kids / Big Kid</option>
              <option value="infant">Infant / Toddler</option>
            </select>
          </div>
          {mode === 'convert' ? (
            <>
              <div className="field">
                <label>Size System</label>
                <select value={from} onChange={e => setFrom(e.target.value)} data-testid="shoe-system">
                  {cols.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Size</label>
                <select value={value} onChange={e => setValue(e.target.value)} data-testid="shoe-value">
                  {options.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="field">
                <label>Foot Length</label>
                <input type="number" value={length} onChange={e => setLength(e.target.value)} data-testid="shoe-length" />
              </div>
              <div className="field">
                <label>Unit</label>
                <select value={lengthUnit} onChange={e => setLengthUnit(e.target.value)}>
                  <option value="cm">cm — centimeter</option>
                  <option value="mm">mm — millimeter</option>
                  <option value="in">in — inch</option>
                </select>
              </div>
              <p className="text-xs text-slate-500">Tip: Stand on paper, mark heel and longest toe, measure the distance.</p>
            </>
          )}
        </div>

        <div>
          {row ? (
            <table className="data-table" data-testid="shoe-result">
              <thead><tr><th>Country / Standard</th><th className="text-right">Size</th></tr></thead>
              <tbody>
                {cols.map(c => (
                  <tr key={c}><td>{c === 'CM' ? 'Length (cm)' : c === 'US' ? '🇺🇸 US' : c === 'UK' ? '🇬🇧 UK' : c === 'EU' ? '🇪🇺 EU' : c === 'JP' ? '🇯🇵 JP' : c}</td><td className="text-right">{row[c]}</td></tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-slate-500 text-sm">Select a size or foot length to convert.</div>
          )}
        </div>
      </div>
    </CalcShell>
  );
}
